import {
  AdminEventJourneyDto,
  AdminEventJourneyUserDto,
  AuthenticatedAccountDto,
  AdminDto,
  CreateAdminRequestDto,
  AppLaunchDto,
  CustomerDto,
  VenueDto,
  VenueDetailDto,
  VenueOpeningHoursDto,
  CreateVenueRequestDto,
  UpdateVenueLocationRequestDto,
  VenueSourceCandidatesResponseDto,
  VisitDto,
  UserDto,
  BlockDto,
  ConversationDto,
  LikeDto,
  VenueSpecialDto,
  VenuePeekDto,
  UploadVenueLogoRequest,
  ScheduledBlastListItemDto,
  AdminLockAccountRequestDto,
  AdminUnlockAccountRequestDto,
  AdminClearLoginFailedAttemptsRequestDto,
  AuditLogDto,
  NotificationLogDto,
  RunScheduledPresetsNowResultDto,
  UserNotificationTriggerDiagnosisDto,
  IntentionDto,
  CreateIntentionRequestDto,
  UpdateIntentionRequestDto,
  AdminVenueBreadcrumbDto,
  AdminVenueBreadcrumbDetailDto,
  AdminUserBreadcrumbActivityDto,
  AdminStickerDto,
  AdminStickerDetailDto,
  AdminConversationDto,
  AdminEventRsvpDto,
  AdminVenueEventDto,
  AdminVisitPlanListItemDto,
  AdminTicketListItemDto,
  AdminRefundRequestDto,
  RefundRequestStatus,
  AdminPartneredHotspotDto,
  CreatePartneredHotspotRequestDto,
  UpdatePartneredHotspotRequestDto,
  AdminWarnUserRequestDto,
  AdminUpdateAccountKindRequestDto,
  FeatureListItemDto,
  FeatureFlagKey,
  DevOtpDto,
  DevTelephoneNumberDto,
  CreateDevTelephoneNumberRequestDto,
  UpdateDevTelephoneNumberRequestDto,
  SimulatedPersonDto,
  CreateSimulatedPersonRequestDto,
  UpdateSimulatedPersonRequestDto,
  SimulatedPresenceDto,
  SpawnSimulatedPersonsAtVenueRequestDto,
  SpawnSimulatedPersonsRandomRequestDto,
  SpawnSimulatedPersonsResultDto,
  RoleDto,
  CreateRoleRequestDto,
  UpdateRoleRequestDto,
  GlobalSearchResultDto,
  NotificationPresetDto,
  CreateNotificationPresetRequestDto,
  UpdateNotificationPresetRequestDto,
  PhotoVerificationAttemptDetailDto,
  PhotoVerificationAttemptListItemDto,
  RegistrationReminderPreviewDto,
  RegistrationReminderSendResultDto,
  AdminSendTestPushResultDto,
  CommentDto,
  CommentDetailDto,
  FEEDBACK_ATTACHMENTS_FIELD,
  AppStoreReviewDto,
  VenueSuggestionDto,
  EventbriteEventImportDto,
  EventbriteOrganizationEventDto,
  EventbriteBulkImportItemDto,
  EventbriteBulkImportResultDto,
  EventbriteVenueAddResultDto,
  VenueEventVisibility,
  EventRsvpBlastAudienceDto,
  EventRsvpBlastRequestDto,
  EventRsvpBlastResultDto,
  AdminContentReportListItemDto,
  ContentReportStatus,
  StickerSettingsConfig,
} from '@ivy-monorepo/shared'
import { apiClient } from './api-client'
import { UserDetailDto, MyPlacesListDto, MyPlaceProximityAction } from '@ivy-monorepo/shared'

class AdminService {
  async getRegistrationReminderPreview(): Promise<RegistrationReminderPreviewDto> {
    const response = await apiClient.get('/admin/registration-reminders/preview')
    return response.data
  }

  async sendRegistrationReminders(payload: {
    message?: string
    limit?: number
    batchIntervalMs?: number
  }): Promise<RegistrationReminderSendResultDto> {
    const response = await apiClient.post('/admin/registration-reminders/send', payload)
    return response.data
  }

  async sendTestPush(publicId: string, message?: string, heading?: string): Promise<AdminSendTestPushResultDto> {
    const response = await apiClient.post(`/admin/users/${publicId}/test-push`, { message, heading })
    return response.data
  }

  async getNotificationPresetMeta(): Promise<{
    triggers: string[]
    variants: string[]
    triggerLabels: Record<string, string>
    triggerDescriptions: Record<string, string>
    triggerCategories: string[]
    triggerCategoryLabels: Record<string, string>
    triggerToCategory: Record<string, string>
    variantLabels: Record<string, string>
    templateVariables: Record<string, string[]>
    templateVariablesByTrigger?: Record<string, string[]>
    defaultNotificationTypes?: Record<string, string>
    onboardingVariants?: string[]
    venueVariants?: string[]
    conversationFollowUpVariants?: string[]
    deepLinkTargets?: string[]
    deepLinkTargetLabels?: Record<string, string>
    checkoutTypes?: string[]
    checkoutTypeLabels?: Record<string, string>
  }> {
    const response = await apiClient.get('/admin/notification-presets/meta')
    return response.data
  }

  async getNotificationPresets(): Promise<NotificationPresetDto[]> {
    const response = await apiClient.get('/admin/notification-presets')
    return response.data
  }

  async createNotificationPreset(dto: CreateNotificationPresetRequestDto): Promise<NotificationPresetDto> {
    const response = await apiClient.post('/admin/notification-presets', dto)
    return response.data
  }

  async updateNotificationPreset(publicId: string, dto: UpdateNotificationPresetRequestDto): Promise<NotificationPresetDto> {
    const response = await apiClient.patch(`/admin/notification-presets/${publicId}`, dto)
    return response.data
  }

  async deleteNotificationPreset(publicId: string): Promise<void> {
    await apiClient.delete(`/admin/notification-presets/${publicId}`)
  }

  async getDevTelephones(): Promise<DevTelephoneNumberDto[]> {
    const response = await apiClient.get('/admin/dev-telephones')
    return response.data
  }

  async createDevTelephone(dto: CreateDevTelephoneNumberRequestDto): Promise<DevTelephoneNumberDto> {
    const response = await apiClient.post('/admin/dev-telephones', dto)
    return response.data
  }

  async updateDevTelephone(publicId: string, dto: UpdateDevTelephoneNumberRequestDto): Promise<DevTelephoneNumberDto> {
    const response = await apiClient.patch(`/admin/dev-telephones/${publicId}`, dto)
    return response.data
  }

  async deleteDevTelephone(publicId: string): Promise<void> {
    await apiClient.delete(`/admin/dev-telephones/${publicId}`)
  }

  async getSimulatedPersons(): Promise<SimulatedPersonDto[]> {
    const response = await apiClient.get('/admin/simulated-persons')
    return response.data
  }

  async createSimulatedPerson(dto: CreateSimulatedPersonRequestDto): Promise<SimulatedPersonDto> {
    const response = await apiClient.post('/admin/simulated-persons', dto)
    return response.data
  }

  async updateSimulatedPerson(publicId: string, dto: UpdateSimulatedPersonRequestDto): Promise<SimulatedPersonDto> {
    const response = await apiClient.patch(`/admin/simulated-persons/${publicId}`, dto)
    return response.data
  }

  async uploadSimulatedPersonImage(file: File): Promise<string> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post('/admin/simulated-persons/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data.imageUrl
  }

  async deleteSimulatedPerson(publicId: string): Promise<void> {
    await apiClient.delete(`/admin/simulated-persons/${publicId}`)
  }

  async getSimulatedPresence(): Promise<SimulatedPresenceDto[]> {
    const response = await apiClient.get('/admin/simulated-presence')
    return response.data
  }

  async spawnSimulatedPersonsAtVenue(
    dto: SpawnSimulatedPersonsAtVenueRequestDto,
  ): Promise<SpawnSimulatedPersonsResultDto> {
    const response = await apiClient.post('/admin/simulated-presence/spawn-at-venue', dto)
    return response.data
  }

  async spawnSimulatedPersonsRandom(
    dto: SpawnSimulatedPersonsRandomRequestDto,
  ): Promise<SpawnSimulatedPersonsResultDto> {
    const response = await apiClient.post('/admin/simulated-presence/spawn-random', dto)
    return response.data
  }

  async clearAllSimulatedPresence(): Promise<{ cleared: number }> {
    const response = await apiClient.delete('/admin/simulated-presence')
    return response.data
  }

  async clearSimulatedPresenceAtVenue(venuePublicId: string): Promise<{ cleared: number }> {
    const response = await apiClient.delete(`/admin/simulated-presence/by-venue/${venuePublicId}`)
    return response.data
  }

  async despawnSimulatedPerson(personPublicId: string): Promise<void> {
    await apiClient.delete(`/admin/simulated-presence/by-person/${personPublicId}`)
  }

  async getSimulatedPersonAuditLogs(
    page = 1,
    pageSize = 10,
    search = '',
    sortOrder: 'asc' | 'desc' = 'desc',
  ): Promise<{ auditLogs: AuditLogDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortOrder,
    })

    const response = await apiClient.get(`/admin/simulated-persons/audit-logs?${params.toString()}`)

    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }

    return { auditLogs: response.data, pagination }
  }

  async getDevTelephoneAuditLogs(
    page = 1,
    pageSize = 10,
    search = '',
    sortOrder: 'asc' | 'desc' = 'desc',
  ): Promise<{ auditLogs: AuditLogDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortOrder,
    })

    const response = await apiClient.get(`/admin/dev-telephones/audit-logs?${params.toString()}`)

    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }

    return { auditLogs: response.data, pagination }
  }

  async getDevOtp(): Promise<DevOtpDto> {
    const response = await apiClient.get(`/admin/dev-otp`)
    return response.data
  }

  async getFeatures(): Promise<FeatureListItemDto[]> {
    const response = await apiClient.get(`/admin/features`)
    return response.data
  }

  async setFeatureValue(key: FeatureFlagKey, value: any): Promise<FeatureListItemDto> {
    const response = await apiClient.patch(`/admin/features/${key}`, { value })
    return response.data
  }

  /** Uploads a custom SVG icon for a new-map sidebar filter; returns the URL to put in `mapActions`. */
  async uploadMapActionIcon(file: File): Promise<string> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post(`/admin/features/map-actions/icon`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data.iconUrl
  }

  async getCheckedInUsers(): Promise<UserDto[]> {
    const response = await apiClient.get(`/admin/users/checkedin`)

    response.data.forEach((user: UserDto) => {
      if (user.currentVenue) {
        user.currentVenue.logoUrl = user.currentVenue.logoUrl ?? `/images/venues/${user.currentVenue.type?.toLowerCase()}.png`
      }
    })

    return response.data
  }

  async getBlocks(blockingUserPublicId?: string): Promise<BlockDto[]> {
    const params = blockingUserPublicId
      ? `?blockingUserPublicId=${encodeURIComponent(blockingUserPublicId)}`
      : ''
    const response = await apiClient.get(`/admin/blocks${params}`)
    return response.data
  }

  async getAuthenticatedAccounts(): Promise<AuthenticatedAccountDto[]> {
    const response = await apiClient.get(`/admin/accounts/authenticated`)
    return response.data
  }

  async getAdmins(): Promise<AdminDto[]> {
    const response = await apiClient.get(`/admin/admins`)
    return response.data
  }

  async createAdmin(dto: CreateAdminRequestDto): Promise<AdminDto> {
    const response = await apiClient.post(`/admin/admins`, dto)
    return response.data
  }

  async getRoles(): Promise<RoleDto[]> {
    const response = await apiClient.get(`/admin/roles`)
    return response.data
  }

  async createRole(dto: CreateRoleRequestDto): Promise<RoleDto> {
    const response = await apiClient.post(`/admin/roles`, dto)
    return response.data
  }

  async updateRole(publicId: string, dto: UpdateRoleRequestDto): Promise<RoleDto> {
    const response = await apiClient.patch(`/admin/roles/${publicId}`, dto)
    return response.data
  }

  async getCustomers(): Promise<CustomerDto[]> {
    const response = await apiClient.get(`/admin/customers`)
    return response.data
  }

  async getUsers(
    page = 1,
    pageSize = 10,
    search = '',
    sortBy = 'lastName',
    sortOrder = 'asc',
    status?: string,
    isInternal?: boolean,
  ): Promise<{ users: UserDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortBy,
      sortOrder,
    })

    if (status && status.length > 0) {
      params.set('status', status)
    }

    if (typeof isInternal === 'boolean') {
      params.set('isInternal', String(isInternal))
    }

    const response = await apiClient.get(`/admin/users?${params.toString()}`)

    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }

    return {
      users: response.data,
      pagination,
    }
  }

  async getUserDetail(publicId: string): Promise<UserDetailDto> {
    const response = await apiClient.get(`/admin/users/${publicId}`)
    return response.data
  }

  async getUserMyPlaces(publicId: string): Promise<MyPlacesListDto> {
    const response = await apiClient.get(`/admin/users/${publicId}/my-places`)
    return response.data
  }

  async unlockUser(publicId: string, reason: string): Promise<UserDetailDto> {
    const dto: AdminUnlockAccountRequestDto = { reason }
    const response = await apiClient.post(`/admin/users/${publicId}/unlock`, dto)
    return response.data
  }

  async clearLoginFailedAttempts(publicId: string, reason: string): Promise<UserDetailDto> {
    const dto: AdminClearLoginFailedAttemptsRequestDto = { reason }
    const response = await apiClient.post(`/admin/users/${publicId}/clear-login-failed-attempts`, dto)
    return response.data
  }

  async lockUser(publicId: string, dto: AdminLockAccountRequestDto): Promise<UserDetailDto> {
    const response = await apiClient.post(`/admin/users/${publicId}/lock`, dto)
    return response.data
  }

  async getAuditLogs(
    page = 1,
    pageSize = 10,
    search = '',
    sortOrder: 'asc' | 'desc' = 'desc',
    eventTypes?: string,
    accountPublicId?: string,
    venuePublicId?: string,
  ): Promise<{ auditLogs: AuditLogDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortOrder,
    })

    if (eventTypes) {
      params.set('eventTypes', eventTypes)
    }

    if (accountPublicId) {
      params.set('accountPublicId', accountPublicId)
    }

    if (venuePublicId) {
      params.set('venuePublicId', venuePublicId)
    }

    const response = await apiClient.get(`/admin/audit-logs?${params.toString()}`)

    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }

    return { auditLogs: response.data, pagination }
  }

  async getNotificationLogs(
    page = 1,
    pageSize = 10,
    search = '',
    sortOrder: 'asc' | 'desc' = 'desc',
    statuses?: string,
    userPublicId?: string,
  ): Promise<{ notificationLogs: NotificationLogDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortOrder,
    })

    if (statuses) {
      params.set('statuses', statuses)
    }

    if (userPublicId) {
      params.set('userPublicId', userPublicId)
    }

    const response = await apiClient.get(`/admin/notification-logs?${params.toString()}`)

    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }

    return { notificationLogs: response.data, pagination }
  }

  async runScheduledPresetsNow(lookbackSeconds?: number): Promise<RunScheduledPresetsNowResultDto> {
    const params = lookbackSeconds ? `?lookbackSeconds=${lookbackSeconds}` : ''
    const response = await apiClient.post(`/admin/notification-presets/run-scheduled-triggers${params}`)
    return response.data
  }

  async getScheduledTriggerDiagnosis(userPublicId: string): Promise<UserNotificationTriggerDiagnosisDto> {
    const response = await apiClient.get(
      `/admin/notification-presets/trigger-diagnosis?userPublicId=${encodeURIComponent(userPublicId)}`
    )
    return response.data
  }

  async getAppLaunches(
    page = 1,
    pageSize = 10,
    search = '',
    sortBy = 'createdAt',
    sortOrder: 'asc' | 'desc' = 'desc'
  ): Promise<{ appLaunches: AppLaunchDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortBy,
      sortOrder,
    })

    const response = await apiClient.get(`/admin/app-launches?${params.toString()}`)

    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }

    return { appLaunches: response.data, pagination }
  }

  async getVenues(page = 1, pageSize = 10, search = '', sortBy = 'name', sortOrder = 'asc', status?: string): Promise<{ venues: VenueDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortBy,
      sortOrder,
    })

    if (status && status.length > 0) {
      params.set('status', status)
    }

    const response = await apiClient.get(`/admin/venues?${params.toString()}`)

    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }

    return {
      venues: response.data,
      pagination,
    }
  }

  async getVenueSuggestions(page = 1, pageSize = 10, status = 'Pending'): Promise<{ suggestions: VenueSuggestionDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      status,
    })

    const response = await apiClient.get(`/admin/venue-suggestions?${params.toString()}`)

    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }

    return {
      suggestions: response.data,
      pagination,
    }
  }

  async approveVenueSuggestion(publicId: string, venue: CreateVenueRequestDto): Promise<VenueDto> {
    const response = await apiClient.post<VenueDto>(`/admin/venue-suggestions/${publicId}/approve`, venue)
    return response.data
  }

  async rejectVenueSuggestion(publicId: string): Promise<VenueSuggestionDto> {
    const response = await apiClient.post<VenueSuggestionDto>(`/admin/venue-suggestions/${publicId}/reject`)
    return response.data
  }

  /**
   * Cross-system search returning grouped results (venues, checked-in users,
   * visited users, users). Uses the new /admin/search/global endpoint.
   */
  async globalSearch(search: string, limit = 5, signal?: AbortSignal): Promise<GlobalSearchResultDto> {
    const params = new URLSearchParams({
      search,
      limit: limit.toString(),
    })

    const response = await apiClient.get(`/admin/search/global?${params.toString()}`, { signal })
    return response.data
  }

  async getVenueBreadcrumbs(
    page = 1,
    pageSize = 10,
    search = '',
    sortBy = 'createdAt',
    sortOrder = 'desc',
    filters: { userPublicId?: string; venuePublicId?: string } = {},
  ): Promise<{ breadcrumbs: AdminVenueBreadcrumbDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortBy,
      sortOrder,
    })

    if (filters.userPublicId) {
      params.set('userPublicId', filters.userPublicId)
    }

    if (filters.venuePublicId) {
      params.set('venuePublicId', filters.venuePublicId)
    }

    const response = await apiClient.get(`/admin/venue-breadcrumbs?${params.toString()}`)

    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }

    return {
      breadcrumbs: response.data,
      pagination,
    }
  }

  async getVenueBreadcrumbDetail(publicId: string): Promise<AdminVenueBreadcrumbDetailDto> {
    const response = await apiClient.get(`/admin/venue-breadcrumbs/${publicId}`)
    return response.data
  }

  async getUserBreadcrumbActivity(
    userPublicId: string,
    page = 1,
    pageSize = 10,
  ): Promise<{ activity: AdminUserBreadcrumbActivityDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    })

    const response = await apiClient.get(`/admin/users/${userPublicId}/breadcrumb-activity?${params.toString()}`)

    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }

    return {
      activity: response.data,
      pagination,
    }
  }

  async deleteVenueBreadcrumb(publicId: string): Promise<void> {
    await apiClient.delete(`/admin/venue-breadcrumbs/${publicId}`)
  }

  async flagVenueBreadcrumb(publicId: string): Promise<void> {
    await apiClient.post(`/admin/venue-breadcrumbs/${publicId}/flag`)
  }

  async unflagVenueBreadcrumb(publicId: string): Promise<void> {
    await apiClient.delete(`/admin/venue-breadcrumbs/${publicId}/flag`)
  }

  async deleteVenueBreadcrumbReply(publicId: string): Promise<void> {
    await apiClient.delete(`/admin/venue-breadcrumb-replies/${publicId}`)
  }

  async flagVenueBreadcrumbReply(publicId: string): Promise<void> {
    await apiClient.post(`/admin/venue-breadcrumb-replies/${publicId}/flag`)
  }

  async unflagVenueBreadcrumbReply(publicId: string): Promise<void> {
    await apiClient.delete(`/admin/venue-breadcrumb-replies/${publicId}/flag`)
  }

  async getStickers(
    page = 1,
    pageSize = 10,
    search = '',
    sortBy = 'createdAt',
    sortOrder = 'desc',
    filters: { userPublicId?: string; venuePublicId?: string } = {},
  ): Promise<{ stickers: AdminStickerDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortBy,
      sortOrder,
    })

    if (filters.userPublicId) {
      params.set('userPublicId', filters.userPublicId)
    }

    if (filters.venuePublicId) {
      params.set('venuePublicId', filters.venuePublicId)
    }

    const response = await apiClient.get(`/admin/stickers?${params.toString()}`)

    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }

    return {
      stickers: response.data,
      pagination,
    }
  }

  async getStickerDetail(publicId: string): Promise<AdminStickerDetailDto> {
    const response = await apiClient.get(`/admin/stickers/${publicId}`)
    return response.data
  }

  async getUserStickerActivity(
    userPublicId: string,
    page = 1,
    pageSize = 10,
  ): Promise<{ activity: AdminStickerDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    })

    const response = await apiClient.get(`/admin/users/${userPublicId}/sticker-activity?${params.toString()}`)

    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }

    return {
      activity: response.data,
      pagination,
    }
  }

  async deleteSticker(publicId: string): Promise<void> {
    await apiClient.delete(`/admin/stickers/${publicId}`)
  }

  async flagSticker(publicId: string): Promise<void> {
    await apiClient.post(`/admin/stickers/${publicId}/flag`)
  }

  async unflagSticker(publicId: string): Promise<void> {
    await apiClient.delete(`/admin/stickers/${publicId}/flag`)
  }

  async getStickerSettings(): Promise<StickerSettingsConfig> {
    const response = await apiClient.get('/admin/stickers/settings')
    return response.data
  }

  async setStickerSettings(config: StickerSettingsConfig): Promise<StickerSettingsConfig> {
    const response = await apiClient.put('/admin/stickers/settings', config)
    return response.data
  }

  /** Pin ("Pinned by the Venue") or unpin many moments at once. */
  async setStickersPinned(publicIds: string[], pinned: boolean): Promise<number> {
    const response = await apiClient.post('/admin/stickers/pin', { publicIds, pinned })
    return response.data?.changed ?? 0
  }

  /** Users' reports on notes and moments (open = waiting for review). */
  async getContentReports(
    page = 1,
    pageSize = 20,
    status: ContentReportStatus = ContentReportStatus.Open,
  ): Promise<{ reports: AdminContentReportListItemDto[]; pagination: any }> {
    const params = new URLSearchParams({ page: page.toString(), pageSize: pageSize.toString(), status })
    const response = await apiClient.get(`/admin/content-reports?${params.toString()}`)
    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '20'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }
    return { reports: response.data, pagination }
  }

  async flagMessage(messageId: number): Promise<void> {
    await apiClient.post(`/admin/messages/${messageId}/flag`)
  }

  async unflagMessage(messageId: number): Promise<void> {
    await apiClient.delete(`/admin/messages/${messageId}/flag`)
  }

  async deleteMessage(messageId: number): Promise<void> {
    await apiClient.delete(`/admin/messages/${messageId}`)
  }

  async warnUser(publicId: string, dto: AdminWarnUserRequestDto): Promise<void> {
    await apiClient.post(`/admin/users/${publicId}/warn`, dto)
  }

  async getVenueDetail(publicId: string): Promise<VenueDetailDto> {
    const response = await apiClient.get(`/admin/venues/${publicId}`)
    return response.data
  }

  async setVenueMapVisibility(venuePublicId: string, isHidden: boolean): Promise<VenueDetailDto> {
    const response = await apiClient.put(`/admin/venues/${venuePublicId}/map-visibility`, { isHidden })
    return response.data
  }

  async closeVenue(venuePublicId: string): Promise<VenueDetailDto> {
    const response = await apiClient.post(`/admin/venues/${venuePublicId}/close`)
    return response.data
  }

  async reopenVenue(venuePublicId: string): Promise<VenueDetailDto> {
    const response = await apiClient.post(`/admin/venues/${venuePublicId}/reopen`)
    return response.data
  }

  async updateVenueName(venuePublicId: string, name: string): Promise<VenueDetailDto> {
    const response = await apiClient.put(`/admin/venues/${venuePublicId}/name`, { name })
    return response.data
  }

  async updateVenueType(venuePublicId: string, type: string): Promise<VenueDetailDto> {
    const response = await apiClient.put(`/admin/venues/${venuePublicId}/type`, { type })
    return response.data
  }

  async updateVenueLocation(venuePublicId: string, dto: UpdateVenueLocationRequestDto): Promise<VenueDetailDto> {
    const response = await apiClient.put(`/admin/venues/${venuePublicId}/location`, dto)
    return response.data
  }

  async updateVenueSpotlight(
    venuePublicId: string,
    spotlightStartDate: string | null,
    spotlightEndDate: string | null,
  ): Promise<VenueDetailDto> {
    const response = await apiClient.put(`/admin/venues/${venuePublicId}/spotlight`, {
      spotlightStartDate,
      spotlightEndDate,
    })
    return response.data
  }

  async updateVenueOpeningHours(venuePublicId: string, openingHours: VenueOpeningHoursDto | null): Promise<VenueDetailDto> {
    const response = await apiClient.put(`/admin/venues/${venuePublicId}/opening-hours`, { openingHours })
    return response.data
  }

  async getVenueVisits(publicId: string): Promise<VisitDto[]> {
    const response = await apiClient.get(`/admin/venues/${publicId}/visits`)
    return response.data
  }

  async getVenueSpecials(publicId: string): Promise<VenueSpecialDto[]> {
    const response = await apiClient.get(`/admin/venues/${publicId}/specials`)
    return response.data
  }

  async getVenueBlasts(venuePublicId: string,): Promise<ScheduledBlastListItemDto[]> {
    const response = await apiClient.get(`/admin/venues/${venuePublicId}/blasts`)
    return response.data
  }

  async createVenueSpecial(venuePublicId: string, data: Partial<VenueSpecialDto>): Promise<VenueSpecialDto[]> {
    const response = await apiClient.post(`/admin/venues/${venuePublicId}/specials`, data)
    return response.data
  }

  async updateUserVisibility(publicId: string, state: boolean): Promise<UserDetailDto> {
    const response = await apiClient.put(`/admin/users/${publicId}/visibility`, { state, })
    return response.data
  }

  async updateAllowRemoteCheckIn(publicId: string, state: boolean): Promise<UserDetailDto> {
    const response = await apiClient.put(`/admin/users/${publicId}/allow-remote-check-in`, { state })
    return response.data
  }

  async updateUserName(publicId: string, firstName: string, lastName: string): Promise<UserDetailDto> {
    const response = await apiClient.put(`/admin/users/${publicId}/name`, { firstName, lastName })
    return response.data
  }

  async updateUserPhotoVerified(publicId: string, state: boolean): Promise<UserDetailDto> {
    const response = await apiClient.put(`/admin/users/${publicId}/verified`, { state })
    return response.data
  }

  async updateUserPhoto(publicId: string, file: File): Promise<UserDetailDto> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.put(`/admin/users/${publicId}/photo`, formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    })
    return response.data
  }

  /** Moderation: deletes the user's photo and clears the verified badge. */
  async removeUserPhoto(publicId: string): Promise<UserDetailDto> {
    const response = await apiClient.delete(`/admin/users/${publicId}/photo`)
    return response.data
  }

  async updateMyPlaceProximityAction(publicId: string, action: MyPlaceProximityAction): Promise<UserDetailDto> {
    const response = await apiClient.put(`/admin/users/${publicId}/my-place-proximity-action`, { action })
    return response.data
  }

  async updateAccountKind(publicId: string, dto: AdminUpdateAccountKindRequestDto): Promise<UserDetailDto> {
    const response = await apiClient.put(`/admin/users/${publicId}/account-kind`, dto)
    return response.data
  }

  async updateVenueSpecial(venuePublicId: string, specialPublicId: string, data: Partial<VenueSpecialDto>): Promise<VenueSpecialDto[]> {
    const response = await apiClient.put(`/admin/venues/${venuePublicId}/specials/${specialPublicId}`, data)
    return response.data;
  }

  async deleteVenueSpecial(venuePublicId: string, specialPublicId: string): Promise<void> {
    await apiClient.delete(`/admin/venues/${venuePublicId}/specials/${specialPublicId}`)
  }

  async updateVenueLogo(venuePublicId: string, logoFile: File, data: UploadVenueLogoRequest): Promise<void> {
    const formData = new FormData()
    formData.append('file', logoFile)
    for (const [key, value] of Object.entries(data)) {
      formData.append(key, value)
    }
    await apiClient.put(`/admin/venues/${venuePublicId}/logo`, formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    })
  }

  async resetVenueLogo(venuePublicId: string): Promise<void> {
    await apiClient.delete(`/admin/venues/${venuePublicId}/logo`)
  }

  async uploadVenueBanner(venuePublicId: string, bannerFile: File): Promise<string> {
    const formData = new FormData()
    formData.append('file', bannerFile)
    const response = await apiClient.put(`/admin/venues/${venuePublicId}/banner`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data.bannerUrl
  }

  async deleteVenueBanner(venuePublicId: string): Promise<void> {
    await apiClient.delete(`/admin/venues/${venuePublicId}/banner`)
  }

  async getVenuePeeks(publicId: string): Promise<VenuePeekDto[]> {
    const response = await apiClient.get(`/admin/venues/${publicId}/peeks`)
    return response.data
  }

  async getVisitDetail(publicId: string): Promise<VisitDto> {
    const response = await apiClient.get(`/admin/visits/${publicId}`)
    return response.data
  }

  async getVisits(
    page = 1,
    pageSize = 10,
    search = '',
    sortBy = 'checkedInAt',
    sortOrder = 'desc',
    period?: string,
    userPublicId?: string,
  ): Promise<{ visits: VisitDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortBy,
      sortOrder,
    })

    if (period && period.length > 0) {
      params.set('period', period)
    }

    if (userPublicId) {
      params.set('userPublicId', userPublicId)
    }

    const response = await apiClient.get(`/admin/visits?${params.toString()}`)

    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }

    return {
      visits: response.data,
      pagination,
    }
  }

  async getLikes(
    page = 1,
    pageSize = 10,
    search = '',
    sortBy = 'likedAt',
    sortOrder = 'desc',
    likingUserPublicId?: string,
  ): Promise<{ likes: LikeDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortBy,
      sortOrder,
    })

    if (likingUserPublicId) {
      params.set('likingUserPublicId', likingUserPublicId)
    }

    const response = await apiClient.get(`/admin/likes?${params.toString()}`)

    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }

    return {
      likes: response.data,
      pagination,
    }
  }

  async getConversations(period?: string): Promise<ConversationDto[]> {
    const params = new URLSearchParams()
    if (period && period.length > 0) {
      params.set('period', period)
    }
    const query = params.toString()
    const response = await apiClient.get(`/admin/conversations${query ? `?${query}` : ''}`)
    return response.data
  }

  async getConversationsPaginated(
    page = 1,
    pageSize = 10,
    search = '',
    sortBy = 'createdAt',
    sortOrder = 'desc',
    filters: { userPublicId?: string; dateFrom?: string; dateTo?: string; flaggedOnly?: boolean } = {}
  ): Promise<{ conversations: AdminConversationDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortBy,
      sortOrder,
    })
    if (filters.userPublicId) params.set('userPublicId', filters.userPublicId)
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom)
    if (filters.dateTo) params.set('dateTo', filters.dateTo)
    if (filters.flaggedOnly) params.set('flaggedOnly', 'true')

    const response = await apiClient.get(`/admin/conversations?${params.toString()}`)
    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }
    return { conversations: response.data, pagination }
  }

  async getConversationDetail(publicId: string): Promise<AdminConversationDto> {
    const response = await apiClient.get(`/admin/conversations/${publicId}`)
    return response.data
  }

  async getVenueSourceCandidates(
    params: {
      southwestLatitude: string
      southwestLongitude: string
      northeastLatitude: string
      northeastLongitude: string
      searchTerms?: string
      includeOverture?: boolean
      includeOsm?: boolean
    },
    signal?: AbortSignal
  ): Promise<VenueSourceCandidatesResponseDto> {
    const response = await apiClient.get<VenueSourceCandidatesResponseDto>('/admin/venue-candidates', {
      params,
      signal,
    })
    return response.data
  }

  async createVenue(venue: CreateVenueRequestDto): Promise<VenueDto> {
    const response = await apiClient.post<VenueDto>('/admin/venues', venue)
    return response.data
  }

  async getVenuesFromGooglePlaces(searchTerms?: string, signal?: AbortSignal) {
    const params = new URLSearchParams(
      Object.entries({ searchTerms })
        .filter(([, v]) => v != null && v !== '' && v.toString().trim().length > 0)
        .map(([k, v]) => [k, String(v)])
    )
    const query = params.toString()
    const response = await apiClient.get(`/admin/google-venues${query ? `?${query}` : ''}`, {
      signal
    })
    return response.data
  }

  async addGoogleVenue(venue: VenueDto): Promise<VenueDto> {
    const response = await apiClient.post(`/admin/google-venues`, venue)
    return response.data
  }

  async blastVenue(
    venuePublicId: string,
    message: string,
    radius: number,
    isTest: boolean,
    openPeekToastOnClick: boolean,
    deepLinkTarget?: 'none' | 'venuePeek' | 'eventsTab' | 'upcomingEvent',
    deepLinkVenueSpecialPublicId?: string | null,
    scheduledDate?: string | null
  ): Promise<number> {
    const dto = {
      message,
      radius,
      isTest,
      scheduledDate,
      openPeekToastOnClick,
      deepLinkTarget,
      deepLinkVenueSpecialPublicId: deepLinkVenueSpecialPublicId ?? null,
    }
    const response = await apiClient.post(`/admin/venues/${venuePublicId}/blasts`, dto)

    const resMessage: string = response.data?.message ?? ''
    const match = resMessage.match(/(\d+)/)
    return match ? parseInt(match[1], 10) : 0
  }

  async calculateUsersInVenueRadius(venuePublicId: string, radius: number): Promise<number> {
    const response = await apiClient.get(`/admin/venues/${venuePublicId}/calculate-users`, {
      params: { radius }
    })

    return response.data
  }

  async updateScheduledBlast(
    blastPublicId: string,
    dto: {
      message: string
      radius: number
      scheduledDate: string
      deepLinkTarget: 'none' | 'venuePeek' | 'eventsTab' | 'upcomingEvent'
      deepLinkVenueSpecialPublicId: string | null
    },
  ): Promise<ScheduledBlastListItemDto> {
    const response = await apiClient.put(`/admin/venues/blasts/${blastPublicId}`, {
      ...dto,
      openPeekToastOnClick: dto.deepLinkTarget !== 'none',
    })

    return response.data
  }

  async deleteScheduledBlast(blastPublicId: string): Promise<boolean> {
    const response = await apiClient.delete(`/admin/venues/blasts/${blastPublicId}`)

    return response.data
  }

  async getIntentions(): Promise<IntentionDto[]> {
    const response = await apiClient.get('/admin/intentions')
    return response.data
  }

  async createIntention(dto: CreateIntentionRequestDto): Promise<IntentionDto> {
    const response = await apiClient.post('/admin/intentions', dto)
    return response.data
  }

  async updateIntention(publicId: string, dto: UpdateIntentionRequestDto): Promise<IntentionDto> {
    const response = await apiClient.patch(`/admin/intentions/${publicId}`, dto)
    return response.data
  }

  async deleteIntention(publicId: string): Promise<void> {
    await apiClient.delete(`/admin/intentions/${publicId}`)
  }

  async reorderIntentions(publicIds: string[]): Promise<IntentionDto[]> {
    const response = await apiClient.put('/admin/intentions/reorder', { publicIds })
    return response.data
  }

  async getIntentionAuditLogs(
    page = 1,
    pageSize = 10,
    search = '',
    sortOrder: 'asc' | 'desc' = 'desc',
  ): Promise<{ auditLogs: AuditLogDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortOrder,
    })

    const response = await apiClient.get(`/admin/intentions/audit-logs?${params.toString()}`)

    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }

    return { auditLogs: response.data, pagination }
  }

  async downloadEventAnalyticsReport(specialPublicId: string): Promise<void> {
    const response = await apiClient.get(`/admin/reports/events/${specialPublicId}`, {
      responseType: 'blob',
    })

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition']
    let filename = `event-analytics-${specialPublicId}.xlsx`

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1]
      }
    }

    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  async getVenueEvents(
    page = 1,
    pageSize = 10,
    search = '',
    sortBy = 'eventEndDate',
    sortOrder: 'asc' | 'desc' = 'desc',
    activeOnly = false,
  ): Promise<{ events: AdminVenueEventDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortBy,
      sortOrder,
      activeOnly: activeOnly.toString(),
    })

    const response = await apiClient.get(`/admin/events?${params.toString()}`)

    return {
      events: response.data,
      pagination: {
        total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
        page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
        pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
        totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
        hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
        hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
      },
    }
  }

  /** Who an RSVP blast for the event would reach (counts only). */
  async getEventRsvpBlastAudience(
    eventPublicId: string,
    options: { excludeTicketHolders: boolean; includeInternal: boolean },
  ): Promise<EventRsvpBlastAudienceDto> {
    const params = new URLSearchParams({
      excludeTicketHolders: String(options.excludeTicketHolders),
      includeInternal: String(options.includeInternal),
    })
    const response = await apiClient.get(`/admin/events/${eventPublicId}/rsvp-blast/audience?${params.toString()}`)
    return response.data
  }

  /** Push to everyone who RSVP'd to the event, deep-linking to it. */
  async sendEventRsvpBlast(eventPublicId: string, body: EventRsvpBlastRequestDto): Promise<EventRsvpBlastResultDto> {
    const response = await apiClient.post(`/admin/events/${eventPublicId}/rsvp-blast`, body)
    return response.data
  }

  async getEventRsvps(
    page = 1,
    pageSize = 10,
    search = '',
    sortBy = 'rsvpedAt',
    sortOrder: 'asc' | 'desc' = 'desc',
    venuePublicId?: string,
    filters: { eventPublicId?: string; source?: string } = {},
  ): Promise<{ rsvps: AdminEventRsvpDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortBy,
      sortOrder,
    })

    if (venuePublicId) {
      params.set('venuePublicId', venuePublicId)
    }
    if (filters.eventPublicId) {
      params.set('eventPublicId', filters.eventPublicId)
    }
    if (filters.source) {
      params.set('source', filters.source)
    }

    const response = await apiClient.get(`/admin/events/rsvps?${params.toString()}`)

    return {
      rsvps: response.data,
      pagination: {
        total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
        page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
        pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
        totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
        hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
        hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
      },
    }
  }

  async syncVisitPlanToGoogleCalendar(publicId: string): Promise<{ alreadySynced: boolean }> {
    const response = await apiClient.post(`/admin/visit-plans/${publicId}/sync-to-google-calendar`)
    return response.data
  }

  /** Refund requests: PENDING first, then PROCESSING, FAILED, then resolved (newest first within each). */
  async getRefundRequests(
    page = 1,
    pageSize = 10,
    statuses: RefundRequestStatus[] = [],
  ): Promise<{ refundRequests: AdminRefundRequestDto[]; pagination: any }> {
    const params = new URLSearchParams({ page: page.toString(), pageSize: pageSize.toString() })
    if (statuses.length > 0) {
      params.set('status', statuses.join(','))
    }
    const response = await apiClient.get(`/admin/refund-requests?${params.toString()}`)
    return {
      refundRequests: response.data,
      pagination: {
        total: parseInt(response.headers['x-total-count'] || '0'),
        page: parseInt(response.headers['x-page'] || '1'),
        pageSize: parseInt(response.headers['x-page-size'] || pageSize.toString()),
        totalPages: parseInt(response.headers['x-total-pages'] || '0'),
        hasNext: response.headers['x-has-next'] === 'true',
        hasPrev: response.headers['x-has-prev'] === 'true',
      },
    }
  }

  async getRefundRequest(publicId: string): Promise<AdminRefundRequestDto> {
    const response = await apiClient.get<AdminRefundRequestDto>(`/admin/refund-requests/${publicId}`)
    return response.data
  }

  async approveRefundRequest(publicId: string, note?: string): Promise<AdminRefundRequestDto> {
    const response = await apiClient.post<AdminRefundRequestDto>(`/admin/refund-requests/${publicId}/approve`, { note })
    return response.data
  }

  async rejectRefundRequest(publicId: string, note?: string): Promise<AdminRefundRequestDto> {
    const response = await apiClient.post<AdminRefundRequestDto>(`/admin/refund-requests/${publicId}/reject`, { note })
    return response.data
  }

  async getTickets(
    page = 1,
    pageSize = 10,
    search = '',
    sortBy = 'purchasedAt',
    sortOrder: 'asc' | 'desc' = 'desc',
    filters: { userPublicId?: string; eventPublicId?: string } = {},
  ): Promise<{ tickets: AdminTicketListItemDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortBy,
      sortOrder,
    })

    if (filters.userPublicId) {
      params.set('userPublicId', filters.userPublicId)
    }
    if (filters.eventPublicId) {
      params.set('eventPublicId', filters.eventPublicId)
    }

    const response = await apiClient.get(`/admin/tickets?${params.toString()}`)

    return {
      tickets: response.data,
      pagination: {
        total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
        page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
        pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
        totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
        hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
        hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
      },
    }
  }

  async getEventJourney(eventPublicId: string): Promise<AdminEventJourneyDto> {
    const response = await apiClient.get(`/admin/events/${eventPublicId}/journey`)
    return response.data
  }

  async getEventJourneyUsers(
    eventPublicId: string,
    page = 1,
    pageSize = 20,
    filter: 'converted' | 'all' = 'converted',
    userPublicId?: string,
  ): Promise<{ users: AdminEventJourneyUserDto[]; pagination: { total: number; page: number; totalPages: number } }> {
    const params = new URLSearchParams({ page: page.toString(), pageSize: pageSize.toString(), filter })
    if (userPublicId) {
      params.set('userPublicId', userPublicId)
    }
    const response = await apiClient.get(`/admin/events/${eventPublicId}/journey/users?${params.toString()}`)
    return {
      users: response.data,
      pagination: {
        total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
        page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
        totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      },
    }
  }

  async getPartneredHotspots(): Promise<AdminPartneredHotspotDto[]> {
    const response = await apiClient.get('/admin/partnered-hotspots')
    return response.data
  }

  async createPartneredHotspot(dto: CreatePartneredHotspotRequestDto): Promise<AdminPartneredHotspotDto> {
    const response = await apiClient.post('/admin/partnered-hotspots', dto)
    return response.data
  }

  async updatePartneredHotspot(publicId: string, dto: UpdatePartneredHotspotRequestDto): Promise<AdminPartneredHotspotDto> {
    const response = await apiClient.patch(`/admin/partnered-hotspots/${publicId}`, dto)
    return response.data
  }

  async deletePartneredHotspot(publicId: string): Promise<void> {
    await apiClient.delete(`/admin/partnered-hotspots/${publicId}`)
  }

  async getVisitPlans(
    page = 1,
    pageSize = 10,
    search = '',
    sortBy = 'plannedDate',
    sortOrder: 'asc' | 'desc' = 'desc',
    venuePublicId?: string,
  ): Promise<{ visitPlans: AdminVisitPlanListItemDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortBy,
      sortOrder,
    })

    if (venuePublicId) {
      params.set('venuePublicId', venuePublicId)
    }

    const response = await apiClient.get(`/admin/visit-plans?${params.toString()}`)

    return {
      visitPlans: response.data,
      pagination: {
        total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
        page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
        pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
        totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
        hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
        hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
      },
    }
  }

  async getEventComments(
    page = 1,
    pageSize = 10,
    search = '',
    sortBy = 'createdAt',
    sortOrder: 'asc' | 'desc' = 'desc',
    venuePublicId?: string,
    eventPublicId?: string,
  ) {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortBy,
      sortOrder,
    })

    if (venuePublicId) {
      params.set('venuePublicId', venuePublicId)
    }

    if (eventPublicId) {
      params.set('eventPublicId', eventPublicId)
    }

    const response = await apiClient.get(`/admin/events/comments?${params.toString()}`)

    return {
      comments: response.data,
      pagination: {
        total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
        page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
        pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
        totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
        hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
        hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
      },
    }
  }

  async getEventCommentDetail(publicId: string) {
    const response = await apiClient.get(`/admin/events/comments/${publicId}`)
    return response.data
  }

  async updateEventComment(publicId: string, content: string) {
    const response = await apiClient.patch(`/admin/events/comments/${publicId}`, { content })
    return response.data
  }

  async deleteEventComment(publicId: string): Promise<void> {
    await apiClient.delete(`/admin/events/comments/${publicId}`)
  }

  async getEventLikes(
    page = 1,
    pageSize = 10,
    search = '',
    sortBy = 'createdAt',
    sortOrder: 'asc' | 'desc' = 'desc',
    venuePublicId?: string,
    eventPublicId?: string,
  ) {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortBy,
      sortOrder,
    })

    if (venuePublicId) {
      params.set('venuePublicId', venuePublicId)
    }

    if (eventPublicId) {
      params.set('eventPublicId', eventPublicId)
    }

    const response = await apiClient.get(`/admin/events/likes?${params.toString()}`)

    return {
      likes: response.data,
      pagination: {
        total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
        page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
        pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
        totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
        hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
        hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
      },
    }
  }

  async deleteEventLike(id: number): Promise<void> {
    await apiClient.delete(`/admin/events/likes/${id}`)
  }

  async getEventCommentLikes(
    page = 1,
    pageSize = 10,
    search = '',
    sortBy = 'createdAt',
    sortOrder: 'asc' | 'desc' = 'desc',
    venuePublicId?: string,
    eventPublicId?: string,
  ) {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
      sortBy,
      sortOrder,
    })

    if (venuePublicId) {
      params.set('venuePublicId', venuePublicId)
    }

    if (eventPublicId) {
      params.set('eventPublicId', eventPublicId)
    }

    const response = await apiClient.get(`/admin/events/comment-likes?${params.toString()}`)

    return {
      commentLikes: response.data,
      pagination: {
        total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
        page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
        pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
        totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
        hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
        hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
      },
    }
  }

  async deleteEventCommentLike(id: number): Promise<void> {
    await apiClient.delete(`/admin/events/comment-likes/${id}`)
  }

  /** Reads an Eventbrite event (link or ID) mapped to the event form's fields. Nothing is saved. */
  async importEventbriteEvent(url: string): Promise<EventbriteEventImportDto> {
    const response = await apiClient.post('/admin/eventbrite/import', { url })
    return response.data
  }

  /** Upcoming events of Ivy's Eventbrite organization, each with the Ivy events already linked to it. */
  async listEventbriteEvents(): Promise<EventbriteOrganizationEventDto[]> {
    const response = await apiClient.get('/admin/eventbrite/events')
    return response.data
  }

  /** Creates Ivy events at the given venues from Eventbrite events; already linked ones come back as skipped. */
  async importEventbriteEventsToVenues(
    items: EventbriteBulkImportItemDto[],
    visibility: VenueEventVisibility,
  ): Promise<EventbriteBulkImportResultDto[]> {
    const response = await apiClient.post('/admin/eventbrite/import-to-venues', { items, visibility })
    return response.data
  }

  async addEventbriteEventVenue(eventbriteId: string): Promise<EventbriteVenueAddResultDto> {
    const response = await apiClient.post(`/admin/eventbrite/events/${encodeURIComponent(eventbriteId)}/add-venue`)
    return response.data
  }

  async uploadEventFlyer(venuePublicId: string, specialPublicId: string, flyerFile: File): Promise<string> {
    const formData = new FormData()
    formData.append('file', flyerFile)
    const response = await apiClient.put(`/admin/venues/${venuePublicId}/specials/${specialPublicId}/flyer`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data.flyerUrl
  }

  async deleteEventFlyer(venuePublicId: string, specialPublicId: string): Promise<void> {
    await apiClient.delete(`/admin/venues/${venuePublicId}/specials/${specialPublicId}/flyer`)
  }

  async getPhotoVerificationAttempts(
    page = 1,
    pageSize = 10,
    search = '',
  ): Promise<{ attempts: PhotoVerificationAttemptListItemDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      search,
    })

    const response = await apiClient.get(`/admin/photo-verification-attempts?${params.toString()}`)

    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }

    return {
      attempts: response.data,
      pagination,
    }
  }

  async getUserPhotoVerificationAttempts(
    userPublicId: string,
    page = 1,
    pageSize = 10,
  ): Promise<{ attempts: PhotoVerificationAttemptListItemDto[]; pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    })

    const response = await apiClient.get(
      `/admin/users/${userPublicId}/photo-verification-attempts?${params.toString()}`,
    )

    const pagination = {
      total: parseInt(response.headers['x-total-count'] || response.headers['X-Total-Count'] || '0'),
      page: parseInt(response.headers['x-page'] || response.headers['X-Page'] || '1'),
      pageSize: parseInt(response.headers['x-page-size'] || response.headers['X-Page-Size'] || '10'),
      totalPages: parseInt(response.headers['x-total-pages'] || response.headers['X-Total-Pages'] || '0'),
      hasNext: (response.headers['x-has-next'] || response.headers['X-Has-Next']) === 'true',
      hasPrev: (response.headers['x-has-prev'] || response.headers['X-Has-Prev']) === 'true',
    }

    return {
      attempts: response.data,
      pagination,
    }
  }

  async getPhotoVerificationAttempt(publicId: string): Promise<PhotoVerificationAttemptDetailDto> {
    const response = await apiClient.get(`/admin/photo-verification-attempts/${publicId}`)
    return response.data
  }

  async manuallyVerifyPhoto(publicId: string): Promise<PhotoVerificationAttemptDetailDto> {
    const response = await apiClient.post(`/admin/photo-verification-attempts/${publicId}/manually-verify`)
    return response.data
  }

  async getFeedbackTopics(): Promise<CommentDto[]> {
    const response = await apiClient.get('/admin/comments')
    return response.data
  }

  async getUserFeedbackTopics(userPublicId: string): Promise<CommentDto[]> {
    const response = await apiClient.get(`/admin/users/${userPublicId}/comments`)
    return response.data
  }

  async getFeedbackTopic(publicId: string): Promise<CommentDetailDto> {
    const response = await apiClient.get(`/admin/comments/${publicId}`)
    return response.data
  }

  async replyToFeedbackTopic(publicId: string, content: string, attachments: File[] = []): Promise<CommentDetailDto> {
    if (attachments.length === 0) {
      const response = await apiClient.post(`/admin/comments/${publicId}/replies`, { content })
      return response.data
    }
    const formData = new FormData()
    formData.append('content', content)
    attachments.forEach(file => formData.append(FEEDBACK_ATTACHMENTS_FIELD, file))
    const response = await apiClient.post(`/admin/comments/${publicId}/replies`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  async closeFeedbackTopic(publicId: string): Promise<CommentDetailDto> {
    const response = await apiClient.post(`/admin/comments/${publicId}/close`)
    return response.data
  }

  async getAppStoreReviews(): Promise<AppStoreReviewDto[]> {
    const response = await apiClient.get('/admin/app-store-reviews')
    return response.data
  }

  async getAppStoreReview(publicId: string): Promise<AppStoreReviewDto> {
    const response = await apiClient.get(`/admin/app-store-reviews/${publicId}`)
    return response.data
  }

  async respondToAppStoreReview(publicId: string, content: string): Promise<AppStoreReviewDto> {
    const response = await apiClient.post(`/admin/app-store-reviews/${publicId}/respond`, { content })
    return response.data
  }

  async syncAppStoreReviews(): Promise<AppStoreReviewDto[]> {
    const response = await apiClient.post('/admin/app-store-reviews/sync')
    return response.data
  }
}

export const adminService = new AdminService()
